import { addDays, format } from "date-fns";
import { compact } from "lodash";
import {
    GetNewsEditRealestates,
    GetNewsEditRealestates_currentRealestate
} from "src/api/generated/GetNewsEditRealestates";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import { isCondominiumNews } from "src/utils/News";

const DATE_INPUT_FORMAT = "yyyy-MM-dd";

export const getDateInputValue = (date: Date): string => format(date, DATE_INPUT_FORMAT);

export const getTomorrowDateInputValue = (date: Date = new Date()): string => getDateInputValue(addDays(date, 1));

export const isPublishfromDateInvalid = (publishfrom?: string | null, today: Date = new Date()): boolean => {
    if (!publishfrom) return false;

    return publishfrom <= getDateInputValue(today);
};

// "2026-11-17" -> "17.11.2026". Anything that is not a date input value is returned as it is.
export const formatPublishfromDate = (publishfrom?: string | null): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(publishfrom ?? "");
    return match ? `${match[3]}.${match[2]}.${match[1]}` : (publishfrom ?? "");
};

export interface INewsRealestateSelection {
    realestates: ISelectableRealestate[];
    // Realestates of the news the user has no access to. They are not shown, but kept when saving.
    inaccessibleRealestateIds: string[];
    // A STWEG news is for coowners, any other news for tenants.
    isCondominium: boolean;
    // An existing news whose realestates the user all has no access to. Its kind is unknown, so saving could send
    // the wrong recipients for the image and the documents. The mask only shows it.
    isReadOnly: boolean;
}

const toSelectableRealestate = (realestate: GetNewsEditRealestates_currentRealestate): ISelectableRealestate => ({
    id: realestate.id,
    name: realestate.name,
    number: realestate.number,
    consolidationid: realestate.consolidationid ?? undefined
});

// An existing news keeps its realestates. A new news starts with the realestate it was opened from, or without
// a realestate when it was opened from the page "News". Such a new news is a STWEG news, because the portal
// only creates STWEG news.
export const getInitialNewsRealestateSelection = ({
    newsrealestates,
    currentRealestate
}: GetNewsEditRealestates): INewsRealestateSelection => {
    if (newsrealestates.length === 0) {
        return {
            realestates: currentRealestate.map(toSelectableRealestate),
            inaccessibleRealestateIds: [],
            isCondominium: isCondominiumNews(currentRealestate) ?? true,
            isReadOnly: false
        };
    }

    const visibleRealestates = compact(newsrealestates.map(({ realestate }) => realestate));

    return {
        realestates: visibleRealestates.map(toSelectableRealestate),
        inaccessibleRealestateIds: newsrealestates
            .filter(({ realestate }) => !realestate)
            .map(({ realestateid }) => realestateid),
        // Not used when the news is read-only, otherwise it comes from a visible realestate.
        isCondominium: isCondominiumNews(visibleRealestates) ?? false,
        isReadOnly: visibleRealestates.length === 0
    };
};

// Micro.News adds the old field realestateid to the list, so with the flag only the list is sent.
// Otherwise the realestate from the URL could never be removed. Without the flag the mask is only reachable
// from a realestate, so realestateid is always set then.
export const getNewsRealestateRequestFields = (
    hasFeatureFlagNews: boolean,
    realestateid: string | undefined,
    realestates: ISelectableRealestate[],
    inaccessibleRealestateIds: string[]
) =>
    hasFeatureFlagNews
        ? { realestateids: [...realestates.map((realestate) => realestate.id), ...inaccessibleRealestateIds] }
        : { realestateid };
